import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { RootState } from 'store/types';
import { MuiBox, MuiContainer, MuiTypography } from 'theme/material-ui';
import { ConnectCareTestMyComputerDialog } from 'components/ConnectCare';
import Screen from 'components/UI/Layout/Screen';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import { getUserAgent } from 'lib/browser';
import { ConnectCareSection } from './ConnectCareSection';
import { ConnectCareModal } from './ConnectCareModal';
import {
  behavioralHealthSectionTitle,
  urgentCareSectionTitle,
  virtualPrimaryCareSectionTitle,
  behavioralHealthModalName,
  primaryCareModalName,
  urgentCareModalName,
  reArrangeSections
} from 'modules/constants/ConnectCare';
import urgentCareImg from 'assets/ConnectCare/urgent-care.jpg';
import behavioralHealthImg from 'assets/ConnectCare/behavioral-health.jpg';
import primaryCareImg from 'assets/ConnectCare/primary-care.jpeg';

import * as bookingActions from 'store/booking/actions';
import * as bookingSelectors from 'store/booking/selectors';
import * as bookingTypes from 'store/booking/types';
import * as findProviderActions from 'store/findProvider/actions';
import * as findProviderConstants from 'store/findProvider/constants';
import { mergeFiltersAndFacets } from 'store/findProvider/utils';
import { ProviderSearchFilters } from 'store/findProvider/types';
import { ActivityStatus } from 'store/amwell/types';
import { setActivityStatus } from 'store/amwell/actions';
import { isImpersonatingSelector } from 'store/account/selectors';
import { profileConsumerHasActiveSHInsuranceSelector } from 'store/profile/selectors';

import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { ConnectCareSection as CCSection } from '../types';

export interface Props extends RouteComponentProps {
  currentUrl: string;
  hasActiveSHInsurance: boolean;
  isProxyUser: boolean;
  patients: bookingTypes.Patient[];
  providerSearch: typeof findProviderActions.providerSearch;
  referringUrl: string;
  setActiveProviderSearchFilter: typeof findProviderActions.setActiveProviderSearchFilter;
  setActivityStatus: (activityStatus: ActivityStatus) => void;
  setPatient: typeof bookingActions.setPatient;
  setSort: typeof findProviderActions.setSortBy;
  setSpecialty: typeof bookingActions.setSpecialty;
  userAgent: IUAParser.IResult;
  validateSelectedPatientInsurance: typeof bookingActions.validateSelectedPatientInsurance;
}

const hasName = (str: string | undefined, name: string) => {
  return str ? str.includes(name) : false;
};

export function ConnectCareLanding(props: Props) {
  const {
    currentUrl,
    hasActiveSHInsurance,
    history,
    isProxyUser,
    patients,
    providerSearch,
    referringUrl,
    setActiveProviderSearchFilter,
    setActivityStatus,
    setPatient,
    setSort,
    setSpecialty,
    userAgent,
    validateSelectedPatientInsurance
  } = props;
  const brand = window.localStorage.getItem('ccBrand');

  const [openTMC, setOpenTMC] = useState(false);
  const [urgentModalOpen, setUrgentModalOpen] = useState(false);
  const [behavioralModalOpen, setBehavioralModalOpen] = useState(false);
  const [primaryCareModalOpen, setPrimaryCareModalOpen] = useState(false);
  const [sections, setSections] = useState<CCSection[]>([]);

  const DEV = process.env.NODE_ENV === 'development';
  const TMCRequired = userAgent ? hasName(userAgent.os?.name, 'Windows') : true;
  const isIE = userAgent ? hasName(userAgent.browser?.name, 'IE') : false;

  useEffect(() => {
    const aux = reArrangeSections(
      brand,
      [urgentCareImg, behavioralHealthImg, primaryCareImg],
      hasActiveSHInsurance
    );
    setSections(aux);
    window.localStorage.removeItem('ccBrand');
  }, []);

  useEffect(() => {
    const fetchPatients = () => {
      bookingActions.getPatients();
    };

    fetchPatients();
  }, []);

  const eventData: AmplitudeEventData = {
    currentUrl,
    referringUrl
  };

  const handlePatientSelect = (patient: bookingTypes.Patient) => {
    setPatient(patient);
    validateSelectedPatientInsurance();
    analyticsService.logEvent(AnalyticsEvent.BookingPatientSelected, eventData);
  };

  const resolveNextStep = () => {
    if (patients.length > 1) {
      history.push('/u/get-care-now/booking/patient-select');
    } else {
      handlePatientSelect(patients[0]);
      history.push('/u/get-care-now/booking/specialty-provider-select');
    }
  };

  const advanceInFlow = (specialtyName: string, specialtyType: string) => {
    setSpecialty({
      nameLong: specialtyName,
      nameShort: specialtyType
    });
    const filters = { isAcceptingOnlineBooking: true, acceptingNewPatients: true };
    const facets = {
      specialty: [
        {
          name: specialtyName,
          group: 'specialty',
          value: specialtyName
        }
      ]
    };
    const dispatchSearchFilters: ProviderSearchFilters = mergeFiltersAndFacets(filters, facets);

    setActiveProviderSearchFilter(dispatchSearchFilters);
    setSort(findProviderConstants.SORT_BY_PROXIMITY);
    providerSearch();
    resolveNextStep();
  };

  const onBehavioralHealthClick = () => {
    const nameShort = Object.values(bookingTypes.TelebehavioralHealthSpecialties).toString();
    advanceInFlow('Behavioral Health Connect Care', nameShort);
  };

  const onPrimaryCareClick = () => {
    const nameShort = Object.values(bookingTypes.VirtualPrimaryCareSpecialties).toString();
    advanceInFlow('Virtual Primary Care', nameShort);
  };

  const handlingStartClick = (url: string, title: string) => {
    if (title === urgentCareSectionTitle && isProxyUser) {
      setActivityStatus(ActivityStatus.PROXY_SWITCH);
    } else if (title === urgentCareSectionTitle && TMCRequired && !isIE) {
      setOpenTMC(true);
    } else if (title === urgentCareSectionTitle) {
      history.push(url);
    } else if (title === behavioralHealthSectionTitle) {
      onBehavioralHealthClick();
    } else if (title === virtualPrimaryCareSectionTitle) {
      onPrimaryCareClick();
    }

    analyticsService.logEvent(AnalyticsEvent.ConnectCarePreVisitFormsStart, {
      referringUrl,
      currentUrl
    });
  };

  const handleOpenModal = (name: string) => {
    switch (name) {
      case urgentCareModalName:
        return setUrgentModalOpen(!urgentModalOpen);
      case behavioralHealthModalName:
        return setBehavioralModalOpen(!behavioralModalOpen);
      case primaryCareModalName:
        return setPrimaryCareModalOpen(!primaryCareModalOpen);
      default:
    }
    return null;
  };

  return (
    <Screen>
      <MuiContainer maxWidth="lg">
        {DEV ? (
          <MuiBox display="flex" justifyContent="center" p={2}>
            <MuiTypography>
              <Link to="/u/get-care-now/connect-care/navigator">Click here</Link> to access
              developer utility page
            </MuiTypography>
          </MuiBox>
        ) : null}

        {sections.map(({ modal, ...rest }) =>
          rest.showCard ? (
            <MuiContainer key={rest.key}>
              <ConnectCareSection
                testId={`${rest.key}-test`}
                {...rest}
                connectCareTypeClick={() => handlingStartClick(rest.redirectTo.web, rest.title)}
                linkHandlerClick={() => handleOpenModal(modal.name)}
              />
              <ConnectCareModal
                key={modal.name}
                open={
                  modal.name === 'urgent-care-modal'
                    ? urgentModalOpen
                    : modal.name === 'behavioral-health-modal'
                    ? behavioralModalOpen
                    : primaryCareModalOpen
                }
                testId={`${modal.name}-test`}
                {...modal}
                onClick={() => handleOpenModal(modal.name)}
              />
              <ConnectCareTestMyComputerDialog
                open={openTMC}
                onClose={() => setOpenTMC(false)}
                onDone={() =>
                  TMCRequired && history.push('/u/get-care-now/connect-care/form/location')
                }
              />
            </MuiContainer>
          ) : null
        )}
      </MuiContainer>
    </Screen>
  );
}

const mapStateToProps = (state: RootState) => ({
  currentUrl: currentLocationPathNameSelector(state),
  hasActiveSHInsurance: profileConsumerHasActiveSHInsuranceSelector(state),
  isProxyUser: isImpersonatingSelector(state),
  patients: bookingSelectors.patientsDataSelector(state),
  referringUrl: previousLocationPathNameSelector(state),
  userAgent: getUserAgent()
});

const mapDispatchToProps = {
  providerSearch: findProviderActions.providerSearch,
  setActiveProviderSearchFilter: findProviderActions.setActiveProviderSearchFilter,
  setActivityStatus: (status: ActivityStatus) => setActivityStatus(status),
  setPatient: bookingActions.setPatient,
  setSort: findProviderActions.setSortBy,
  setSpecialty: bookingActions.setSpecialty,
  validateSelectedPatientInsurance: bookingActions.validateSelectedPatientInsurance
};

export default connect(mapStateToProps, mapDispatchToProps)(ConnectCareLanding);
